import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock, useI18n } from "@/coquille-bridge";
import { useAuth } from "@/context/AuthContext.jsx";
import { ensureKarateR0Session } from "@/api/karateR0SessionBridge.js";
import {
  formationGetContinue,
  formationGetDiscovery,
  formationGetProgress,
  formationGetTrackProgress,
  formationListTracks,
  formationMarkIntroViewed,
  formationUpdateDiscovery,
} from "@/api/karateR0FormationApi.js";
import { pickFormationText, formationStatusLabel } from "./karateFormationLocale.js";
import { getKarateFormationUi } from "./karateFormationUi.js";
import { getFormationIntroContent } from "./karateFormationIntroContent.js";
import { KarateFormationDiscovery } from "./KarateFormationDiscovery.jsx";
import { KarateFormationIntroPage } from "./KarateFormationIntroPage.jsx";
import { KarateFormationRunner } from "./KarateFormationRunner.jsx";
import { StaticFormationPreview } from "./StaticFormationPreview.jsx";
import "@/formation/karate-formation.css";

function isLiveFormationRole(role) {
  return role === "hanshi" || role === "admin" || role === "sensei";
}

export function KarateFormationCockpitPage() {
  const { role, isAuthenticated, authSource, username } = useAuth();
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const ui = getKarateFormationUi(locale);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const trackParam = searchParams.get("track");
  const stepParam = searchParams.get("step");

  const [loadState, setLoadState] = useState("idle");
  const [error, setError] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [progressList, setProgressList] = useState([]);
  const [activeTrackDetail, setActiveTrackDetail] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [discoveryScreens, setDiscoveryScreens] = useState([]);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [introReviewMode, setIntroReviewMode] = useState(false);

  const liveRole = isAuthenticated && isLiveFormationRole(role);
  const introUi = getFormationIntroContent(locale);

  const reload = useCallback(async () => {
    if (!liveRole) return;
    setLoadState("loading");
    setError(null);
    const session = await ensureKarateR0Session({ auth: { role, username, authSource } });
    if (!session.ok) {
      setLoadState("error");
      setError(ui.sessionError);
      return;
    }

    const [tracksRes, progressRes, discoveryRes, continueRes] = await Promise.all([
      formationListTracks(),
      formationGetProgress(),
      formationGetDiscovery(),
      formationGetContinue(),
    ]);

    if (!tracksRes.response.ok) {
      setLoadState("error");
      setError(tracksRes.body?.error || ui.sessionError);
      return;
    }

    setTracks(tracksRes.body.tracks ?? []);
    setProgressList(progressRes.body?.progress ?? []);

    if (discoveryRes.response.ok) {
      setDiscovery(discoveryRes.body.discovery);
      setDiscoveryScreens(discoveryRes.body.screens ?? []);
      if (discoveryRes.body.discovery?.shouldShow) setShowDiscovery(true);
    }

    const trackId = trackParam || continueRes.body?.trackId || tracksRes.body.tracks?.[0]?.id;
    if (trackId) {
      const detail = await formationGetTrackProgress(trackId);
      if (detail.response.ok) setActiveTrackDetail(detail.body);
    }

    setLoadState("ready");
  }, [liveRole, role, username, authSource, trackParam, ui.sessionError]);

  useEffect(() => {
    if (liveRole) reload();
    else setLoadState("static");
  }, [liveRole, reload]);

  const primaryTrack = tracks[0];
  const primaryProgress = progressList.find((p) => p.trackId === primaryTrack?.id) ?? progressList[0];

  useEffect(() => {
    if (searchParams.get("intro") === "review") {
      setIntroReviewMode(true);
      setShowIntro(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (loadState !== "ready" || showDiscovery || trackParam || searchParams.get("intro") === "review") return;
    if (primaryProgress?.shouldShowIntro) {
      setIntroReviewMode(false);
      setShowIntro(true);
    }
  }, [loadState, showDiscovery, trackParam, primaryProgress?.shouldShowIntro, searchParams]);

  const runnerTrack = useMemo(() => {
    if (!trackParam || !activeTrackDetail?.track) return null;
    if (activeTrackDetail.track.id !== trackParam) return activeTrackDetail.track;
    return activeTrackDetail.track;
  }, [trackParam, activeTrackDetail]);

  const runnerProgress = progressList.find((p) => p.trackId === trackParam) ?? activeTrackDetail?.progress;

  async function handleDiscoveryUpdate(payload) {
    const { body } = await formationUpdateDiscovery(payload);
    if (body?.discovery) setDiscovery(body.discovery);
  }

  function startStep(trackId, stepId) {
    setSearchParams({ track: trackId, step: stepId });
  }

  function quitRunner() {
    setSearchParams({});
    reload();
  }

  async function handleDiscoveryClose() {
    setShowDiscovery(false);
    if (!primaryTrack) return;
    const detail = await formationGetTrackProgress(primaryTrack.id);
    if (detail.response.ok && detail.body.progress?.shouldShowIntro) {
      setIntroReviewMode(false);
      setShowIntro(true);
    }
  }

  async function handleIntroStart(startTraining) {
    if (!primaryTrack) return;
    if (startTraining) {
      await formationMarkIntroViewed(primaryTrack.id);
      setProgressList((prev) =>
        prev.map((p) =>
          p.trackId === primaryTrack.id ? { ...p, introViewed: true, shouldShowIntro: false } : p,
        ),
      );
      setShowIntro(false);
      setIntroReviewMode(false);
      setSearchParams({});
      const step = primaryProgress?.activeStepId || activeTrackDetail?.track?.steps?.[0]?.id;
      if (step) startStep(primaryTrack.id, step);
      return;
    }
    setShowIntro(false);
    setIntroReviewMode(false);
    setSearchParams({});
  }

  if (!liveRole) {
    return (
      <PageSection title={ui.pageTitle} subtitle={ui.pageSubtitle} testId="page-formation">
        <StaticFormationPreview />
      </PageSection>
    );
  }

  if (loadState === "loading" || loadState === "idle") {
    return (
      <PageSection title={ui.pageTitle} subtitle={ui.pageSubtitle} testId="page-formation">
        <p data-testid="formation-loading">{ui.loading}</p>
      </PageSection>
    );
  }

  if (loadState === "error") {
    return (
      <PageSection title={ui.pageTitle} subtitle={ui.pageSubtitle} testId="page-formation">
        <GsdReadableBlock testId="formation-session-error">
          <p>{error}</p>
          <button type="button" className="btn-primary" data-testid="formation-retry" onClick={reload}>
            {ui.retry}
          </button>
        </GsdReadableBlock>
        <StaticFormationPreview />
      </PageSection>
    );
  }

  if (trackParam && stepParam && runnerTrack && !showIntro) {
    return (
      <PageSection title={ui.pageTitle} subtitle={ui.pageSubtitle} testId="page-formation">
        <KarateFormationRunner
          track={runnerTrack}
          progress={runnerProgress}
          stepId={stepParam}
          locale={locale}
          ui={ui}
          onProgress={(next) => {
            setProgressList((prev) => prev.map((p) => (p.trackId === next.trackId ? next : p)));
            setActiveTrackDetail((prev) => (prev ? { ...prev, progress: next } : prev));
          }}
          onQuit={quitRunner}
        />
      </PageSection>
    );
  }

  const lastStepId = primaryProgress?.activeStepId;
  const lastStep = activeTrackDetail?.track?.steps?.find((s) => s.id === lastStepId);
  const doneCount = primaryProgress?.doneCount ?? (primaryProgress?.completedStepIds ?? []).length;

  return (
    <PageSection title={ui.pageTitle} subtitle={ui.pageSubtitle} testId="page-formation">
      {showDiscovery ? (
        <KarateFormationDiscovery
          screens={discoveryScreens}
          initialScreen={discovery?.currentScreen ?? 0}
          locale={locale}
          ui={ui}
          onUpdate={handleDiscoveryUpdate}
          onClose={handleDiscoveryClose}
        />
      ) : null}

      {showIntro && primaryTrack ? (
        <KarateFormationIntroPage
          trackId={primaryTrack.id}
          progress={primaryProgress}
          locale={locale}
          reviewMode={introReviewMode}
          onStartTraining={handleIntroStart}
        />
      ) : null}

      {!showIntro ? (
      <section className="karate-formation-cockpit" data-testid="formation-live-cockpit">
        <GsdReadableBlock className="karate-formation-hero" testId="formation-my-training">
          <h2>{ui.myTraining}</h2>
          {primaryTrack ? (
            <>
              <p className="karate-formation-hero__title">{pickFormationText(primaryTrack.title, locale)}</p>
              <p className="karate-formation-hero__progress" data-testid="formation-progress-summary">
                {primaryProgress?.doneCount ?? 0} / {primaryProgress?.totalSteps ?? primaryTrack.totalSteps}{" "}
                {locale === "en" ? "steps completed" : "étapes terminées"}
              </p>
              <div className="karate-formation-progress-bar" aria-hidden="true">
                <span style={{ width: `${primaryProgress?.percent ?? 0}%` }} />
              </div>
              <p className="muted">{primaryProgress?.percent ?? 0} %</p>
              {lastStep ? (
                <p data-testid="formation-last-step">
                  {ui.lastStep} : {pickFormationText(lastStep.title, locale)}
                </p>
              ) : null}
              <button
                type="button"
                className="btn-primary"
                data-testid="formation-continue"
                onClick={() => {
                  const step = primaryProgress?.activeStepId || activeTrackDetail?.track?.steps?.[0]?.id;
                  if (step) startStep(primaryTrack.id, step);
                }}
              >
                {ui.continue}
              </button>
            </>
          ) : null}
        </GsdReadableBlock>

        <div className="karate-formation-modules">
          <section data-testid="formation-modules-todo">
            <h3>{ui.modulesTodo}</h3>
            <ul className="karate-formation-module-list">
              {(activeTrackDetail?.track?.steps ?? []).map((step) => {
                const done = (primaryProgress?.completedStepIds ?? []).includes(step.id);
                const active = primaryProgress?.activeStepId === step.id;
                let status = "NOT_STARTED";
                if (done) status = "COMPLETED";
                else if (active) status = "IN_PROGRESS";
                return (
                  <li key={step.id} data-testid={`formation-module-${step.id}`}>
                    <span>{pickFormationText(step.title, locale)}</span>
                    <span className={`karate-formation-status karate-formation-status--${status.toLowerCase()}`}>
                      {formationStatusLabel(status, locale)}
                    </span>
                    <button type="button" className="btn-primary-outline btn-sm" onClick={() => startStep(primaryTrack.id, step.id)}>
                      {locale === "en" ? "Open" : "Ouvrir"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section data-testid="formation-modules-done" className={doneCount === 0 ? "karate-formation-modules-done--empty" : undefined}>
            <h3>{ui.modulesDone}</h3>
            {doneCount === 0 ? (
              <p className="muted" data-testid="formation-modules-done-empty">
                {locale === "en" ? "Completed steps will appear here." : "Vos étapes terminées apparaîtront ici."}
              </p>
            ) : (
            <ul className="karate-formation-module-list">
              {(primaryProgress?.completedStepIds ?? []).map((stepId) => {
                const step = activeTrackDetail?.track?.steps?.find((s) => s.id === stepId);
                if (!step) return null;
                return (
                  <li key={stepId}>
                    <span>{pickFormationText(step.title, locale)}</span>
                    <span className="karate-formation-status karate-formation-status--completed">
                      {formationStatusLabel("COMPLETED", locale)}
                    </span>
                  </li>
                );
              })}
            </ul>
            )}
          </section>
        </div>

        <p className="karate-formation-replay">
          <button
            type="button"
            className="btn-primary-outline"
            data-testid="formation-replay-discovery"
            onClick={async () => {
              await handleDiscoveryUpdate({ replay: true });
              setShowDiscovery(true);
            }}
          >
            {ui.replayDiscovery}
          </button>
          <button
            type="button"
            className="btn-primary-outline"
            data-testid="formation-replay-intro"
            onClick={() => {
              setIntroReviewMode(true);
              setShowIntro(true);
            }}
          >
            {introUi.replayIntro}
          </button>
        </p>
      </section>
      ) : null}

      {!isLiveFormationRole(role) ? null : (
        <p className="muted">
          <Link to="/aide">{locale === "en" ? "Contextual help" : "Aide contextuelle"}</Link>
        </p>
      )}
    </PageSection>
  );
}
